/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

import { IdSchema } from './api.js';
import { OpticsSchema } from './optics.js';
import { Vec3Schema } from './scene-doc.js';

// The agent is told which lenses exist, and reaches the schema only through here.
export { LENSES } from './optics.js';

/**
 * What the assistant may do to devices, and nothing else (devplan §6).
 *
 * A separate, much smaller vocabulary than the scene's, and deliberately so:
 * the Device page owns devices, the Scene page owns scenery, and neither op set
 * can express the other's work. Nothing here reaches a device body — no
 * Creative Look, no shutter, no capture. Where a device stands is visible on
 * screen and can be dragged back; what a device *does* is not this agent's
 * authority. Optics are planning values on the device row (camera-simulation.md
 * §4), so they are the agent's to set: no body ever sees them.
 *
 * Shared, for the same reason the scene ops are: the browser's writer, the
 * agent's tool signatures and the tests all validate against these.
 */

export const AddDeviceOpSchema = z
  .object({
    op: z.literal('add_device'),
    name: z.string().min(1).max(120).optional(),
    model: z.string().min(1).max(60).optional(),
    position: Vec3Schema.optional(),
    rotation: Vec3Schema.optional(),
    /** Where it should point. Resolved to a rotation before anything is written. */
    look_at: Vec3Schema.optional(),
    /** Any subset; the rest is the body's default and is fitted to the lens. */
    optics: OpticsSchema.partial().strict().optional(),
  })
  .strict();

export const SetPoseOpSchema = z
  .object({
    op: z.literal('set_pose'),
    device_id: IdSchema,
    position: Vec3Schema.optional(),
    rotation: Vec3Schema.optional(),
  })
  .strict();

export const LookAtOpSchema = z.object({ op: z.literal('look_at'), device_id: IdSchema, target: Vec3Schema }).strict();

export const RemoveDeviceOpSchema = z.object({ op: z.literal('remove_device'), device_id: IdSchema }).strict();

/** A subset of a device's optics; what is not named is kept, or fitted to a new lens. */
export const SetOpticsOpSchema = z
  .object({
    op: z.literal('set_optics'),
    device_id: IdSchema,
    ...OpticsSchema.partial().shape,
  })
  .strict();

export const DeviceOpSchema = z.discriminatedUnion('op', [
  AddDeviceOpSchema,
  SetPoseOpSchema,
  LookAtOpSchema,
  RemoveDeviceOpSchema,
  SetOpticsOpSchema,
]);
export type DeviceOp = z.infer<typeof DeviceOpSchema>;

/**
 * A device is a real Thing with a real shadow, so creating or destroying one is
 * never applied without a click (devplan §7.4 rule 5). Moving one is a drag the
 * user could do themselves, and is not.
 */
export const touchesTwin = (op: DeviceOp): boolean => op.op === 'add_device' || op.op === 'remove_device';

/** No more devices than a world could plausibly hold (devplan §7.4 rule 4). */
export const MAX_DEVICES = 64;

/**
 * What the device agent is told (devplan §7.3 item 3).
 *
 * Ids, names, models, poses and whether anything is reporting — plus the
 * scene's extent, because "around the capture volume" has to resolve to metres
 * and there is no constant in this codebase that could tell it (devplan §4.7).
 *
 * Never the scene document: this page cannot edit scenery, so the agent is not
 * given the vocabulary to describe it.
 */
export const DeviceDigestEntrySchema = z
  .object({
    device_id: IdSchema,
    name: z.string().max(120),
    model: z.string().max(60),
    /** Absent for a device that has never been placed. */
    position: Vec3Schema.optional(),
    rotation: Vec3Schema.optional(),
    /** Whether any gateway has reported this device's twin. Not whether it is in use. */
    reporting: z.boolean(),
    /**
     * The lens and format, plus what they add up to at the focus distance, so
     * "at most 1 mm per pixel" is arithmetic rather than a guess.
     */
    optics: OpticsSchema.extend({
      horizontal_fov_deg: z.number(),
      vertical_fov_deg: z.number(),
      mm_per_px_at_focus: z.number(),
    })
      .strict()
      .optional(),
  })
  .strict();
export type DeviceDigestEntry = z.infer<typeof DeviceDigestEntrySchema>;

/**
 * How well the rig sees the stand-in subject, when the user has coverage on
 * (camera-simulation.md §12). Shares are of the subject's visible surface, 0 to 1.
 */
export const CoverageDigestSchema = z
  .object({
    subject: z.object({ position: Vec3Schema, height: z.number() }).strict(),
    target_mm_per_px: z.number(),
    min_overlap: z.number().int(),
    covered: z.number(),
    meets_target: z.number(),
    meets_overlap: z.number(),
    coarsest_mm_per_px: z.number().optional(),
    coarsest_part: z.string().max(40).optional(),
    least_covered: z.array(z.object({ part: z.string().max(40), uncovered: z.number() }).strict()).max(5),
  })
  .strict();
export type CoverageDigest = z.infer<typeof CoverageDigestSchema>;

export const DeviceDigestSchema = z
  .object({
    units: z.literal('m'),
    bounds: z.object({ center: Vec3Schema, radius: z.number() }).strict(),
    /** The device the user is looking at, when there is one. */
    selection: IdSchema.optional(),
    devices: z.array(DeviceDigestEntrySchema).max(MAX_DEVICES),
    coverage: CoverageDigestSchema.optional(),
  })
  .strict();
export type DeviceDigest = z.infer<typeof DeviceDigestSchema>;
